import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { DialogShell } from "@/shared/ui/DialogShell";
import {
  useTranscriptActions,
  useTranscriptDraft,
} from "../hooks/useTranscriptDraft";
import { highestConfidenceSamplesBySpeaker } from "../utils/speakerSamples";
import { formatTimestamp } from "../utils/timeFormat";
import { formatSpeakerLabel, getSpeakerColorByLabel } from "./speakerColors";
import { SpeakerLineClip } from "./SpeakerLineClip";
import { VideoData } from "../../../shared/interfaces/VideoData";

interface SpeakerQuickAssignDialogProps {
  open: boolean;
  onClose: () => void;
  /** Source media for the per-line clips; clips are omitted when unavailable. */
  video?: VideoData | null;
}

/**
 * First-open prompt that shows each speaker's highest-confidence sentences and
 * asks the reviewer to name them. Submitting renames every entry for that
 * speaker (via {@link renameSpeaker}), turning raw diarization labels
 * ("Speaker 0") into real names before the reviewer starts editing.
 */
export const SpeakerQuickAssignDialog: React.FC<
  SpeakerQuickAssignDialogProps
> = ({ open, onClose, video }) => {
  const theme = useTheme();
  const { draft, speakers } = useTranscriptDraft();
  const { renameSpeakers } = useTranscriptActions();

  const clipSrc = video?.videoUrl;
  const isAudio = video?.fileType?.startsWith("audio") ?? false;

  // Snapshot the samples when the dialog opens; they're derived from the loaded
  // draft and shouldn't shift as the user types names.
  const samples = useMemo(
    () => (open ? highestConfidenceSamplesBySpeaker(draft) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  const [names, setNames] = useState<Record<string, string>>({});

  const nameOf = (label: string) => (names[label] ?? "").trim();
  // Effective label for *every* current speaker after assign — renamed samples
  // take their entered name; all others (including speakers with no sample shown
  // in this dialog) keep their current label. Renaming two speakers to one name
  // would merge them, so any collision across the full set is flagged and blocks
  // the assign. Building this over all speakers lets a rename vacate its own
  // original name (e.g. a swap A→B, B→A) without falsely flagging it.
  const finalBySpeaker = new Map<string, string>(
    speakers.map((label) => [label, label]),
  );
  for (const s of samples) finalBySpeaker.set(s.label, nameOf(s.label) || s.label);
  const finalLabels = [...finalBySpeaker.values()];
  const duplicateLabels = new Set(
    finalLabels.filter((label, i) => finalLabels.indexOf(label) !== i),
  );

  const changed = samples.filter(
    (s) => nameOf(s.label) && nameOf(s.label) !== s.label,
  );
  const canAssign = changed.length > 0 && duplicateLabels.size === 0;

  const handleAssign = () => {
    // Apply all renames atomically so swaps/chains don't collide mid-flight.
    renameSpeakers(Object.fromEntries(changed.map((s) => [s.label, nameOf(s.label)])));
    onClose();
  };

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      size="sm"
      titleId="speaker-quick-assign-dialog-title"
    >
      <DialogTitle
        id="speaker-quick-assign-dialog-title"
        sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1, pr: 6 }}
      >
        <RecordVoiceOverIcon color="primary" />
        Name the speakers
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Recognize who said these lines? Correctly label the speakers to quickly assign names to the entire transcript.
        </Typography>

        <Stack spacing={2}>
          {samples.map((s) => {
            const finalLabel = nameOf(s.label) || s.label;
            const isDuplicate = duplicateLabels.has(finalLabel);
            const speakerColor = getSpeakerColorByLabel(
              s.label,
              speakers,
              theme.palette.mode,
            );
            return (
              <Box
                key={s.label}
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderLeft: `3px solid ${speakerColor.primary}`,
                  borderRadius: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip
                    label={formatSpeakerLabel(s.label)}
                    size="small"
                    sx={{
                      backgroundColor: speakerColor.soft,
                      color: speakerColor.text,
                      border: `1px solid ${speakerColor.border}`,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      height: 22,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {s.entryCount} {s.entryCount === 1 ? "entry" : "entries"}
                  </Typography>
                </Box>

                <Stack spacing={1} sx={{ mb: 1.5 }}>
                  {s.sentences.map((sentence) => (
                    <Box
                      key={`${sentence.start}-${sentence.end}`}
                      sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                    >
                      {clipSrc && (
                        <SpeakerLineClip
                          src={clipSrc}
                          isAudio={isAudio}
                          start={sentence.start}
                          end={sentence.end}
                        />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "text.secondary",
                            fontFamily: "monospace",
                            fontSize: "0.7rem",
                          }}
                        >
                          {formatTimestamp(sentence.start)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic", color: "text.primary" }}
                        >
                          &ldquo;{sentence.text}&rdquo;
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <TextField
                  value={names[s.label] ?? ""}
                  onChange={(e) =>
                    setNames((prev) => ({ ...prev, [s.label]: e.target.value }))
                  }
                  size="small"
                  fullWidth
                  placeholder="Who said this? e.g. Officer Reyes"
                  error={isDuplicate}
                  helperText={
                    isDuplicate ? "Another speaker already has this name" : " "
                  }
                />
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Skip for now
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!canAssign}
          sx={{ textTransform: "none" }}
        >
          Assign names
        </Button>
      </DialogActions>
    </DialogShell>
  );
};
